/* WeQuote Dev Board — Calendar (renderCalendar + calBarDown/Move/Up, faithful
 * port). Day / week / month timeline on the SAME schedule as the Roadmap:
 * month bands + day cells (W-numbers on Mondays), weekend/block-out shading,
 * a today line, the ✎ Design row (in-design cards spanning design start →
 * deadline, laned into sub-rows), per-dev rows whose overlapping bars (same-
 * day sub-day cards) stack into sub-rows, next-availability per dev, and the
 * gated placeholder bar for still-in-design cards. Drag a bar to move (pins),
 * drag its edges to resize (re-derives the estimate), drop on another dev to
 * reassign; a click with no movement opens the pop-up. Coming-soon side panel
 * lists assigned-but-undated cards in priority order. */
import React, { useRef, useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as CAL from '../core/calendar'
import * as SC from '../core/schedule'
import { suggestScore } from '../core/suggest'
import type { Feature } from '../core/types'
import { DEVCOLS, DL, devColor, RM_MON, SLGet } from '../core/constants'
import { fmtDate, todayISO } from '../core/dates'
import { parseISO, toISO, isBlocked, setScheduleBlockouts, cardStatus } from '../core/logic'
import { TitleWithBug } from './parts'
import { IcFlag, IcPencil, IcCalendar } from '../icons'

interface DragState {
  id: number; mode: 'l' | 'r' | 'move'; x0: number; y0: number; dayW: number
  moved: boolean; el: HTMLElement; l0: number; w0: number; startISO: string; estISO: string
}

export const CalendarView: React.FC = () => {
  const { cards, blockouts, sched, mutate, openCardModal, openSubModal } = useDevBoard()
  const [view, setView] = useState<CAL.CalView>('weeks')
  const [anchorISO, setAnchorISO] = useState<string>(() => toISO(CAL.calMonday(new Date())))
  const [hiddenDevs, setHiddenDevs] = useState<Record<string, boolean>>({})
  const [sideOpen, setSideOpen] = useState(false)
  const dragRef = useRef<DragState | null>(null)

  const cfg = CAL.calCfg(view)
  const numDays = cfg.weeks * 7
  const dayW = cfg.dayW
  const barH = cfg.barH
  const anchor = CAL.calMonday(parseISO(anchorISO))
  const a0 = toISO(anchor)
  const totalW = numDays * dayW
  const todayV = todayISO()
  setScheduleBlockouts(blockouts)

  const days = Array.from({ length: numDays }, (_, i) => {
    const d = new Date(anchor.getTime())
    d.setDate(d.getDate() + i)
    const dw = d.getDay()
    return { i, iso: toISO(d), d, dow: dw, weekend: dw === 0 || dw === 6, mon: d.getMonth(), yr: d.getFullYear(), dom: d.getDate() }
  })

  const geom = (sISO: string, eISO: string) => {
    if (!sISO || !eISO) return null
    const s = SC.daysBetween(a0, sISO)
    const e = SC.daysBetween(a0, eISO)
    if (e < 0 || s > numDays - 1) return null
    const x = Math.max(0, s) * dayW
    const right = Math.min(numDays, e + 1) * dayW
    return { x, w: Math.max(dayW * 0.7, right - x) }
  }

  const Shade: React.FC<{ dev: string | null }> = ({ dev }) => (
    <>
      {days.map((dd) => {
        const bo = dev ? isBlocked(dd.iso, dev) : blockouts.some((b) => b.scope === 'all' && dd.iso >= b.start && dd.iso <= b.end)
        return (
          <React.Fragment key={dd.i}>
            {dd.weekend ? <div className="cal-shade" style={{ left: dd.i * dayW, width: dayW }} /> : bo ? <div className="cal-shade bo" style={{ left: dd.i * dayW, width: dayW }} /> : null}
            {dd.iso === todayV && <div className="cal-today" style={{ left: dd.i * dayW, width: dayW }} />}
          </React.Fragment>
        )
      })}
    </>
  )

  /* ── drag machinery (calBarDown / calDragMove / calDragUp) ── */
  const barDown = (e: React.MouseEvent, f: Feature, sISO: string) => {
    if (e.button !== 0) return
    const el = e.currentTarget as HTMLElement
    const rect = el.getBoundingClientRect()
    const edge = 10
    const mode: DragState['mode'] = e.clientX - rect.left < edge ? 'l' : rect.right - e.clientX < edge ? 'r' : 'move'
    const d: DragState = { id: f.id, mode, x0: e.clientX, y0: e.clientY, dayW, moved: false, el, l0: parseFloat(el.style.left) || 0, w0: parseFloat(el.style.width) || 0, startISO: sISO, estISO: f.estCompletion || sISO }
    dragRef.current = d
    e.preventDefault()
    e.stopPropagation()
    const onMove = (ev: MouseEvent) => {
      const dr = dragRef.current
      if (!dr) return
      if (Math.abs(ev.clientX - dr.x0) > 3 || Math.abs(ev.clientY - dr.y0) > 3) dr.moved = true
      if (!dr.moved) return
      const dd = Math.round((ev.clientX - dr.x0) / dr.dayW)
      if (dr.mode === 'r') dr.el.style.width = Math.max(dr.dayW * 0.7, dr.w0 + dd * dr.dayW) + 'px'
      else if (dr.mode === 'l') { dr.el.style.left = dr.l0 + dd * dr.dayW + 'px'; dr.el.style.width = Math.max(dr.dayW * 0.7, dr.w0 - dd * dr.dayW) + 'px' }
      else { dr.el.style.left = dr.l0 + dd * dr.dayW + 'px'; dr.el.style.opacity = '0.6' }
    }
    const onUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      const dr = dragRef.current
      dragRef.current = null
      if (!dr) return
      dr.el.style.opacity = ''
      if (!dr.moved) { openCardModal(dr.id); return }
      const dd = Math.round((ev.clientX - dr.x0) / dr.dayW)
      if (dr.mode === 'r') {
        mutate((fs) => CAL.calResizeCard(fs, dr.id, sched.start, CAL.calDaysISO(dr.estISO, dd), blockouts))
      } else if (dr.mode === 'l') {
        mutate((fs) => CAL.calMoveStart(fs, dr.id, CAL.calDaysISO(dr.startISO, dd), blockouts))
      } else {
        const el2 = document.elementFromPoint ? document.elementFromPoint(ev.clientX, ev.clientY) : null
        const lane = el2 && (el2 as HTMLElement).closest ? (el2 as HTMLElement).closest('[data-cal-dev]') : null
        const tgt = lane ? lane.getAttribute('data-cal-dev') : null
        const ns = CAL.calDaysISO(dr.startISO, dd)
        const f2 = cards.find((x) => x.id === dr.id)
        if (tgt && f2 && tgt !== f2.dev) mutate((fs) => CAL.calReassign(fs, dr.id, tgt, ns, blockouts))
        else mutate((fs) => CAL.calMoveStart(fs, dr.id, ns, blockouts))
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const Bar: React.FC<{ f: Feature; sISO: string; eISO: string; kind: 'solid' | 'placeholder' | 'design' | 'stage' | 'live'; top: number; stage?: { i: number; done: boolean }; assumed?: boolean }> = ({ f, sISO, eISO, kind, top, stage, assumed }) => {
    const g = geom(sISO, eISO)
    if (!g) return null
    const st = cardStatus(f)
    // Lee, 3 Sep — calendar-only tones: grey = not started · amber = in progress · green = complete
    const tone = kind === 'live' ? 'done' : CAL.calBarTone(f)
    const cls = 'cal-bar cal-t-' + tone + (kind === 'placeholder' ? ' cal-ph' : '') + (kind === 'design' ? ' cal-dsg' : kind === 'stage' ? ' cal-stage' + (stage && stage.done ? ' cal-stage-done' : '') : kind === 'live' ? '' : ' cal-drag') + (assumed ? ' cal-assumed' : '')
    return (
      <div
        className={cls}
        style={{ left: g.x, width: g.w, height: barH, top }}
        title={(kind === 'stage' && stage ? SC.stageLabel(f, stage.i) : f.title + ' — ' + (kind === 'live' ? 'completed' : st.label) + (kind === 'placeholder' ? ' (design in progress)' : '') + (assumed ? ' · no estimate — assumed 1 day' : ''))}
        onClick={kind === 'design' || kind === 'stage' || kind === 'live' ? () => openCardModal(f.id) : undefined}
        onMouseDown={kind === 'design' || kind === 'stage' || kind === 'live' ? undefined : (e) => barDown(e, f, sISO)}
      >
        {f.blocker && <span className="cal-bar-blk"><IcFlag size={11} /></span>}
        {view !== 'months' && <span className="cal-bar-t">{kind === 'stage' && stage ? SC.stageLabel(f, stage.i) : <TitleWithBug feat={f} />}</span>}
        {kind === 'design' && f.dev && DL[f.dev]
          ? <span className="cal-bar-m">→ {DL[f.dev]}</span>
          : view === 'days'
            ? <span className="cal-bar-m">{kind === 'design' ? 'design ' + fmtDate(f.designDeadline) : 'est ' + fmtDate(f.estCompletion)}</span>
            : null}
      </div>
    )
  }

  /* header */
  const bands: { k: string; mon: number; yr: number; n: number }[] = []
  days.forEach((dd) => {
    const k = dd.yr + '-' + dd.mon
    const b = bands[bands.length - 1]
    if (b && b.k === k) b.n++
    else bands.push({ k, mon: dd.mon, yr: dd.yr, n: 1 })
  })
  const dl2 = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

  /* design row */
  const designCards = cards.filter(CAL.calOnDesignRow)
  const dlanes = CAL.calLanes(designCards.map((f) => ({ f, s: f.designStart, e: f.designDeadline })), 's', 'e')
  const designH = Math.max(barH + 18, dlanes.n * (barH + 6) + 12)

  /* dev rows */
  const devs = DEVCOLS.filter((d) => !hiddenDevs[d])
  const shift = view === 'months' ? 4 : view === 'days' ? 1 : 2
  const doShift = (w: number) => { const a = CAL.calMonday(parseISO(anchorISO)); a.setDate(a.getDate() + w * 7); setAnchorISO(toISO(a)) }

  /* coming soon */
  const cs = cards.filter(CAL.calComingSoon)
  const byDev: Record<string, Feature[]> = {}
  cs.forEach((f) => { const k = f.dev && DL[f.dev] ? f.dev : '_un'; (byDev[k] = byDev[k] || []).push(f) })
  const csOrder = DEVCOLS.filter((d) => byDev[d]).concat(byDev['_un'] ? ['_un'] : [])

  return (
    <div id="calendarView" style={{ padding: '16px 24px' }}>
      <div className="cal-toolbar">
        <div className="cal-view-toggle">
          {(['days', 'weeks', 'months'] as CAL.CalView[]).map((v) => (
            <button key={v} className={'cal-vbtn' + (view === v ? ' active' : '')} onClick={() => setView(v)}>{v[0].toUpperCase() + v.slice(1)}</button>
          ))}
        </div>
        <div className="cal-nav">
          <button className="cal-navbtn" onClick={() => doShift(-shift)} title="Back">‹</button>
          <button className="cal-navbtn" onClick={() => setAnchorISO(toISO(CAL.calMonday(new Date())))}>Today</button>
          <button className="cal-navbtn" onClick={() => doShift(shift)} title="Forward">›</button>
        </div>
        <div className="cal-devfilter">
          {DEVCOLS.map((d) => (
            <button key={d} className={'cal-devchip' + (hiddenDevs[d] ? ' off' : '')} onClick={() => setHiddenDevs((h) => ({ ...h, [d]: !h[d] }))}>
              <span className="cal-chip-dot" style={{ background: devColor(d) }} />{DL[d]}
            </button>
          ))}
        </div>
        <button className={'cal-sidebtn' + (sideOpen ? ' active' : '')} onClick={() => setSideOpen((v) => !v)}>Coming soon</button>
      </div>
      <div className="cal-hint">Drag a bar to move it · drag its edges to resize · drag onto another developer to reassign. Everything after it re-flows.</div>
      <div className="cal-wrap">
        <div className="cal-scroll">
          <div className="cal-timeline" style={{ minWidth: totalW + 210 }}>
            <div className="cal-headrow">
              <div className="cal-corner">
                <div className="cal-corner-t">{devs.length} developer{devs.length === 1 ? '' : 's'}</div>
                <div className="cal-corner-s">weekends shaded</div>
              </div>
              <div className="cal-headdays" style={{ width: totalW }}>
                <div className="cal-months">{bands.map((b, i) => <div key={i} className="cal-mon" style={{ width: b.n * dayW }}>{RM_MON[b.mon]} {b.yr}</div>)}</div>
                <div className="cal-daycells">
                  {days.map((dd) => (
                    <div key={dd.i} className={'cal-day' + (dd.weekend ? ' we' : '') + (dd.iso === todayV ? ' now' : '')} style={{ width: dayW }}>
                      {dd.dow === 1 && <span className="cal-wk">W{CAL.calWeekNo(dd.d)}</span>}
                      <span className="cal-dom">{view === 'months' ? String(dd.dom) : dl2[dd.dow] + ' ' + dd.dom}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="cal-row" style={{ height: designH }}>
              <div className="cal-rowlabel cal-designlabel"><div className="cal-lbl-name"><IcPencil size={12} /> Design</div><div className="cal-lbl-sub">{designCards.length} in progress</div></div>
              <div className="cal-lane" style={{ width: totalW }}>
                <Shade dev={null} />
                {dlanes.items.map((it, idx) => <Bar key={it.f.id} f={it.f} sISO={it.s} eISO={it.e} kind="design" top={dlanes.assign[idx] * (barH + 6) + 6} />)}
              </div>
            </div>
            {devs.length === 0 ? (
              <div className="cal-empty">No developers selected. Use the filter to add some.</div>
            ) : (
              devs.map((dev) => {
                const laneCards: { f: Feature; s: string; e: string; stage?: { i: number; done: boolean }; assumed?: boolean; liveBar?: boolean }[] =
                  cards.filter((f) => f.dev === dev && SC.datedCard(f) && !f.multiStage).map((f) => ({ f, s: sched.start[f.id] || f.start, e: f.estCompletion }))
                SC.stageUnits(cards, dev).forEach((u) => laneCards.push({ f: u.f, s: u.s.schedStart, e: u.s.estCompletion, stage: { i: u.i, done: u.s.done } }))
                // Lee, 3 Sep: no-estimate cards with a start (or in a dev column) show anyway, assumed at 1 day
                CAL.calAssumedUnits(cards, dev).forEach((u) => laneCards.push({ f: u.f, s: u.s, e: u.e, assumed: true }))
                // completed cards stay on the calendar over the days they ran
                CAL.calLiveUnits(cards, dev).forEach((u) => laneCards.push({ f: u.f, s: u.s, e: u.e, liveBar: true }))
                const lanes = CAL.calLanes(laneCards, 's', 'e')
                const rowH = Math.max(barH + 30, lanes.n * (barH + 6) + 18)
                const avail = CAL.devNextAvailable(dev, cards, blockouts)
                return (
                  <div key={dev} className="cal-row" style={{ height: rowH }}>
                    <div className="cal-rowlabel">
                      <div className="cal-ava" style={{ background: devColor(dev) }}>{DL[dev].slice(0, 2)}</div>
                      <div className="cal-lbl-txt"><div className="cal-lbl-name">{DL[dev]}</div><div className="cal-lbl-sub">Next availability: {fmtDate(avail)}</div></div>
                    </div>
                    <div className="cal-lane" data-cal-dev={dev} style={{ width: totalW }}>
                      <Shade dev={dev} />
                      {lanes.items.map((it: any, idx: number) => (
                        <Bar key={it.f.id + (it.stage ? ':s' + it.stage.i : it.liveBar ? ':lv' : '')} f={it.f} sISO={it.s} eISO={it.e}
                          kind={it.stage ? 'stage' : it.liveBar ? 'live' : it.f.status === 'design' ? 'placeholder' : 'solid'}
                          stage={it.stage} assumed={it.assumed} top={lanes.assign[idx] * (barH + 6) + 8} />
                      ))}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
        {sideOpen && (
          <div className="cal-side">
            <div className="cal-side-hd">Coming soon <button className="cal-side-x" onClick={() => setSideOpen(false)}>×</button></div>
            <div className="cal-side-note">Assigned but not yet dated · priority order</div>
            {csOrder.length === 0 ? (
              <div className="cal-cs-empty">Nothing queued — every assigned card is scheduled.</div>
            ) : (
              csOrder.map((k) => {
                const list = byDev[k].sort((a, b) => suggestScore(b) - suggestScore(a) || a.po - b.po)
                const shown = list.slice(0, 5)
                const more = list.length - shown.length
                return (
                  <div key={k} className="cal-cs-grp">
                    <div className="cal-cs-hd">{k === '_un' ? 'Unassigned' : DL[k]} <span>{list.length}</span></div>
                    {shown.map((f) => {
                      const st = cardStatus(f)
                      return (
                        <div key={f.id} className="cal-cs-row" onClick={() => openCardModal(f.id)}>
                          <div className="cal-cs-t">{f.title}</div>
                          <div className="cal-cs-sub"><span className={'st-chip st-' + st.cls}>{st.label}</span> {SLGet(f.status)}
                            <button className="sched-mini" title="Schedule" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'schedule', id: f.id }) }}><IcCalendar size={11} /> Schedule</button>
                          </div>
                        </div>
                      )
                    })}
                    {more > 0 && <div className="cal-cs-more">+{more} more</div>}
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>
    </div>
  )
}
